import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { MessageCircle, ChevronUp, ChevronDown } from 'lucide-react';
import MessageBubble from './MessageBubble';
import DateSeparator from './DateSeparator';
import { useChatStore } from '../../store/chatStore';
import { LoadingSpinner } from '../ui';
import { isMobile } from '../../utils/mobileUtils';
import type { ChatMessage } from '../../store/chatStore';

interface MessageListProps {
  groupId: string;
}

const MessageList: React.FC<MessageListProps> = ({ groupId }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const previousMessageCountRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  const {
    getGroupMessages,
    isLoadingHistory,
    isLoadingMore,
    isHistoryLoaded,
    hasMoreHistoryMessages,
    loadMoreMessages,
  } = useChatStore();

  const messages = getGroupMessages(groupId);
  const hasHistory = isHistoryLoaded(groupId);
  const hasMore = hasMoreHistoryMessages(groupId);

  // Check if user is scrolled near the bottom (within 100px for better UX)
  const checkIfNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return false;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < 100; // Reduced threshold for better bottom detection
  };

  // Handle scroll events to track user position
  const handleScroll = useCallback(() => {
    const nearBottom = checkIfNearBottom();
    setIsNearBottom(nearBottom);

    // If user scrolled to bottom, clear new message indicator
    if (nearBottom) {
      setHasNewMessages(false);
    }
  }, [hasNewMessages]); // Include hasNewMessages in dependencies

  // Handle load more messages with scroll position maintenance
  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    // Mark that we're loading pagination (not new real-time messages)
    isLoadingMoreRef.current = true;

    // Store current scroll position and first message for reference
    const container = messagesContainerRef.current;
    const firstMessage = container?.firstElementChild;
    const scrollHeightBefore = container?.scrollHeight || 0;

    await loadMoreMessages(groupId);

    // Mark pagination as complete and update message count reference
    setTimeout(() => {
      isLoadingMoreRef.current = false;
      previousMessageCountRef.current = messages.length; // Update to new count after pagination
    }, 100);

    // Maintain scroll position after new messages are loaded
    if (container && firstMessage) {
      setTimeout(() => {
        const scrollHeightAfter = container.scrollHeight;
        const heightDifference = scrollHeightAfter - scrollHeightBefore;
        container.scrollTop = container.scrollTop + heightDifference;
      }, 50);
    }
  };

  // Group messages by user and time proximity
  const groupConsecutiveMessages = (messages: ChatMessage[]) => {
    const TIME_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

    return messages.map((message, index) => {
      const prevMessage = messages[index - 1];

      // Is this message part of a group (consecutive from same user within time threshold)?
      const isGrouped = prevMessage &&
        prevMessage.userId === message.userId &&
        (new Date(message.createdAt).getTime() - new Date(prevMessage.createdAt).getTime()) < TIME_THRESHOLD;

      return {
        ...message,
        isGrouped, // Don't show timestamp/username if grouped
        isGroupLeader: !isGrouped // Show timestamp/username if this starts a new group
      };
    });
  };

  // Group messages by date
  const groupMessagesByDate = (messages: ChatMessage[]) => {
    const grouped: { date: string; messages: ChatMessage[] }[] = [];

    messages.forEach((message: ChatMessage) => {
      const messageDate = new Date(message.createdAt);
      const dateKey = messageDate.toDateString(); // e.g., "Mon Jan 01 2024"

      let group = grouped.find(g => g.date === dateKey);
      if (!group) {
        group = { date: dateKey, messages: [] };
        grouped.push(group);
      }
      group.messages.push(message);
    });

    return grouped;
  };

  // Apply message grouping first, then group by date
  const messagesWithGrouping = groupConsecutiveMessages(messages);
  const groupedMessages = groupMessagesByDate(messagesWithGrouping);

  // Add scroll event listener to track user position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]); // Include handleScroll in dependencies

  // Smart auto-scroll: only scroll when user is near bottom
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = previousMessageCountRef.current;

    // Check if we actually have new messages (not just initial load or pagination)
    const hasNewMessage = currentMessageCount > previousMessageCount && !isLoadingMoreRef.current;


    if (hasNewMessage) {
      if (isNearBottom && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
        // Clear any existing new message indicator since user will see the new message
        setHasNewMessages(false);
      } else if (!isNearBottom) {
        // User is reading history and new message arrived - show indicator instead of scrolling
        setHasNewMessages(true);
      }
    }

    // Update the previous message count (but delay if we're paginating)
    if (!isLoadingMoreRef.current) {
      previousMessageCountRef.current = currentMessageCount;
    }
  }, [messages.length, isNearBottom]);

  // Force scroll to bottom (for initial load and manual scroll)
  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior,
        block: 'end'
      });
      setHasNewMessages(false);
      setIsNearBottom(true);
    }
  };

  // Position at the bottom instantly when history loads, before paint, so the
  // chat opens at the newest messages without a visible top-to-bottom scroll.
  // Direct scrollTop assignment scrolls only this container (never the page).
  useLayoutEffect(() => {
    if (messages.length > 0 && hasHistory) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
        // Second pass after layout settles (fonts/avatars can shift heights)
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
      setIsNearBottom(true);
    }
  }, [hasHistory]);

  if (isLoadingHistory) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Chatgeschiedenis laden..." />
      </div>
    );
  }

  if (hasHistory && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-fg-muted">
        <div className="text-center">
          <MessageCircle className="mx-auto h-12 w-12 text-fg-faint mb-4" />
          <h3 className="text-lg font-bold text-fg-bright mb-2">Nog geen berichten</h3>
          <p className="text-sm">Wees de eerste die iets stuurt!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={messagesContainerRef}
      className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 min-w-0 relative"
    >
      {/* Load More button - shown at top when there are more messages */}
      {hasHistory && hasMore && (
        <div className="flex justify-center py-4">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className={`
              flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors
              ${isLoadingMore
                ? 'bg-white/[.04] text-fg-muted cursor-not-allowed'
                : 'bg-white/[.05] text-neon-cyan hover:bg-white/[.08]'
              }
            `}
          >
            {isLoadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
                <span>Oudere berichten laden...</span>
              </>
            ) : (
              <>
                <ChevronUp className="h-4 w-4" />
                <span>Laad eerdere berichten</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Messages grouped by date */}
      {groupedMessages.map((group, groupIndex) => (
        <div key={group.date}>
          {/* Show date separator for each group */}
          {group.messages[0]?.createdAt && <DateSeparator date={group.messages[0].createdAt} />}

          {/* Messages for this date */}
          <div>
            {group.messages.map((message: ChatMessage, messageIndex: number) => (
              <MessageBubble
                key={message.id || `temp-${message.userId}-${message.createdAt}-${messageIndex}`}
                message={message}
                isGrouped={(message as any).isGrouped}
                isGroupLeader={(message as any).isGroupLeader}
              />
            ))}
          </div>

          {/* Place scroll reference after the last group with extra padding */}
          {groupIndex === groupedMessages.length - 1 && (
            <div ref={messagesEndRef} className="h-8" />
          )}
        </div>
      ))}

      {/* New messages indicator - fixed at bottom of chat screen */}
      {hasNewMessages && !isNearBottom && (
        <div className={`fixed left-1/2 transform -translate-x-1/2 z-50 ${
          isMobile() ? 'bottom-28' : 'bottom-20'
        }`}>
          <button
            onClick={() => scrollToBottom('smooth')}
            className="bg-neon-gradient text-ink px-4 py-2 rounded-item shadow-neon-pink-strong hover:opacity-90 transition-opacity flex items-center gap-2 animate-pulse"
          >
            <span className="text-sm font-bold">Nieuwe berichten</span>
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Jump to latest button for mobile users who aren't at bottom */}
      {isMobile() && !isNearBottom && !hasNewMessages && messages.length > 0 && (
        <div className="fixed bottom-32 right-4 z-50">
          <button
            onClick={() => scrollToBottom('smooth')}
            className="bg-neon-gradient text-ink p-3 rounded-full shadow-neon-pink-strong hover:opacity-90 transition-opacity"
            title="Naar nieuwste berichten"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageList;